import {BaseStore} from '@src/components/store/base.store';
import {RootStore} from '@src/components/store/index';
import {BronzeTabName, EventType} from '@src/dtos/analytics.dto';
import {Address, CategoryUrl, Domain, FwVersion} from '@src/dtos/version.dto';
import {PresetDto} from '@src/dtos/preset.dto';
import bronzeService from '@src/services/bronze.service';
import {AUTO_CLEAR_TIMEOUT_MS, calcActionDuration, MAX_BRONZE_DOMAINS} from '@src/utils/constants';
import {BronzeVersionState, DisableReasons, PolicyType, VersionState} from '@src/utils/enums';
import {
    getAllBronzeCategoriesListByVersion,
    isVersionActive,
    isVersionPending,
    mapAddresses,
    mapDomains
} from '@src/utils/versionUtils';
import {action, computed, makeObservable, observable, runInAction} from 'mobx';
import cloneDeep from 'lodash/cloneDeep';

export class FwVersionBronzeStore extends BaseStore {
    public selectedFwVersion: FwVersion | undefined = undefined;
    public originalSelectedFwVersion: FwVersion | undefined = undefined;
    public isLoading = false;
    public isDirty = false;
    public selectedVersionDomains: Domain[] = [];
    /** list of PowerDNS category names for this bronze version */
    public bronzeCategories: CategoryUrl[] = [];

    /** Category presets fetched from the API. */
    public presets: PresetDto[] = [];
    public presetsLoading = false;

    /** The groupId of the currently active preset, or null if none is active. */
    public activePresetId: string | null = null;

    // versionState is now managed in the store
    public versionState: BronzeVersionState | undefined = undefined;
    public displayError = false;
    public applyPolicyTime: number | undefined = undefined;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            selectedFwVersion: observable,
            originalSelectedFwVersion: observable,
            isLoading: observable,
            isDirty: observable,
            selectedVersionDomains: observable,
            bronzeCategories: observable,
            presets: observable,
            presetsLoading: observable,
            activePresetId: observable,
            versionState: observable,
            displayError: observable,
            applyPolicyTime: observable,
            getAddresses: computed,
            isSelectedBronzeVersionEditable: computed,
            bronzeDisableReason: computed,
            activePreset: computed,
            addAddress: action,
            updateAddress: action,
            removeAddress: action,
            setVersionState: action,
            displayErrorAndClear: action,
            clearDisplayError: action,
            addDomain: action,
            updateDomain: action,
            removeDomain: action,
            updateBronzeCategory: action,
            blockAllCategories: action,
            allowAllCategories: action,
            activatePreset: action,
            deactivatePreset: action,
            loadPresets: action,
            updateSelectedVersion: action,
            resetFwVersion: action,
            reset: action,
            setApplyPolicyTime: action,
            isPendingVersion: computed,
            isDnsProtectionDeactivated: computed
        });
    }

    public setApplyPolicyTime(time: number | undefined) {
        this.applyPolicyTime = time;
    }

    reset() {
        this.selectedFwVersion = undefined;
        this.originalSelectedFwVersion = undefined;
        this.selectedVersionDomains = [];
        this.bronzeCategories = [];
        this.activePresetId = null;
        this.isLoading = false;
        this.isDirty = false;
        this.versionState = undefined;
        this.displayError = false;
    }

    public setVersionState(state: BronzeVersionState | undefined, options?: { autoClear?: boolean }) {
        this.versionState = state;
        if (options?.autoClear) {
            setTimeout(() => {
                runInAction(() => {
                    this.versionState = undefined;
                });
            }, AUTO_CLEAR_TIMEOUT_MS);
        }
    }

    public displayErrorAndClear() {
        this.displayError = true;
        setTimeout(() => {
            runInAction(() => {
                this.displayError = false;
            });
    }, AUTO_CLEAR_TIMEOUT_MS);
    }

    public clearDisplayError() {
        this.displayError = false;
    }

    public async applyPolicyBronze(standardApplyType?: BronzeTabName): Promise<FwVersion> {
        if (!this.selectedFwVersion) throw new Error('No selected version');
        const { customerId, serviceId } = this.rootStore.customerStore;
        const versionId = this.selectedFwVersion.externalId;
        const addresses = this.selectedFwVersion.content.policy.addresses
            .map(addr => ({ ...addr, description: addr.description ?? '' }));
        const domains = this.selectedVersionDomains;
        const categories = this.bronzeCategories;
        try {
            const result = await bronzeService.applyBronzePolicy(
                customerId,
                serviceId,
                versionId,
                addresses,
                domains,
                categories
            );
            this.updateSelectedVersion(result, true);
            this.sendApplyPolicyEvent(versionId, {standardApplyType});
            return result;
        } catch (e) {
            this.setDirty(true);
            throw e;
        }
    }

    get isPendingVersion() {
        return isVersionPending(this.selectedFwVersion);
    }

    get isDnsProtectionDeactivated() {
        return this.selectedFwVersion?.versionType === PolicyType.DNS_DEACTIVATE;
    }

    get isSelectedBronzeVersionEditable() {
        if (this.rootStore.userStore.isReadOnlyUser) return false;
        if (!this.selectedFwVersion) return false;
        const isPendingExist = isVersionPending(this.selectedFwVersion);
        const isSaving = this.versionState === BronzeVersionState.SAVING;

        const isCurrentActive = isVersionActive(this.selectedFwVersion);
        return (!isPendingExist && !isSaving && isCurrentActive);
    }

    get bronzeDisableReason() {
        if (this.isSelectedBronzeVersionEditable) return undefined;
        if (this.rootStore.userStore.isReadOnlyUser) return DisableReasons.READ_ONLY_USER;
        if (!this.selectedFwVersion) return undefined;

        if (isVersionPending(this.selectedFwVersion) || this.versionState === BronzeVersionState.SAVING) return DisableReasons.PENDING_EXIST;
        return undefined;
    }

    get bronzeDomainsDisableReason() {
        if (this.isDnsProtectionDeactivated) return DisableReasons.DNS_PROTECTION_DEACTIVATED;
        return this.bronzeDisableReason;
    }

    public async loadBronzeVersionData() {
        try {
            this.setLoading(true);
            const results = await Promise.all([
                bronzeService.getLatestVersion(this.rootStore.customerStore.customerId, this.rootStore.customerStore.serviceId),
                this.loadPresets(),
            ]);
            const res = results[0];
            runInAction(() => {
                this.selectedFwVersion = res;
                this.originalSelectedFwVersion = cloneDeep(res);
                this.selectedVersionDomains = mapDomains(res.content.policy.url_categories);
                this.bronzeCategories = getAllBronzeCategoriesListByVersion(res);
                this.activePresetId = this.inferActivePresetId(this.bronzeCategories);
                this.isLoading = false;
                this.setDirty(false);
            });
        } catch (e) {
            this.setLoading(false);
            throw e;
        }
    }

    public async loadPresets(): Promise<void> {
        if (this.presetsLoading || this.presets.length > 0) return;
        runInAction(() => {
            this.presetsLoading = true;
        });
        try {
            const result = await bronzeService.getPresets();
            const normalizedPresets = result
                .map(preset => this.normalizePreset(preset))
                .sort((a, b) => a.groupName.localeCompare(b.groupName));
            runInAction(() => {
                this.presets = normalizedPresets;
                this.activePresetId = this.inferActivePresetId(this.bronzeCategories);
            });
        } finally {
            runInAction(() => {
                this.presetsLoading = false;
            });
        }
    }

    get activePreset(): PresetDto | undefined {
        return this.presets.find(p => p.groupId === this.activePresetId);
    }

    public updateSelectedVersion(version: FwVersion, resetState = false) {
        this.selectedFwVersion = version;
        this.selectedVersionDomains = mapDomains(version.content.policy.url_categories);
        this.bronzeCategories = getAllBronzeCategoriesListByVersion(version);
        this.activePresetId = this.inferActivePresetId(this.bronzeCategories);
        if (resetState) {
            this.originalSelectedFwVersion = cloneDeep(version);
            this.setDirty(false);
        }
    }

    public resetFwVersion() {
        if (this.originalSelectedFwVersion) {
            this.selectedFwVersion = cloneDeep(this.originalSelectedFwVersion);
            this.selectedVersionDomains = mapDomains(this.selectedFwVersion?.content?.policy?.url_categories);
            this.bronzeCategories = getAllBronzeCategoriesListByVersion(this.selectedFwVersion);
            this.activePresetId = null;
            this.setDirty(false);
        }
    }

    /**
     * Activates a preset: sets it as the sole active preset and resets bronzeCategories
     * so only the preset's categories are blocked and all others are allowed.
     * If the same preset is already active, it is deactivated (toggle-off semantics).
     * @param groupId  The groupId of the preset to activate.
     */
    public activatePreset(groupId: string): void {
        // Toggle-off semantics: clicking the active preset deactivates it.
        if (this.activePresetId === groupId) {
            this.deactivatePreset();
            return;
        }

        const preset = this.presets.find(p => p.groupId === groupId);
        if (!preset) {
            console.warn(`[FwVersionBronzeStore] activatePreset: preset "${groupId}" not found.`);
            return;
        }

        const presetCategoryNames = new Set(preset.categories.map(c => c.categoryName.toLowerCase()));
        this.activePresetId = groupId;
        this.bronzeCategories = this.bronzeCategories.map(c => ({
            ...c,
            action: presetCategoryNames.has(c.name.toLowerCase()) ? 'block' : 'allow',
        }));
        this.setDirty(true);
    }

    /**
     * Deactivates the currently active preset: clears activePresetId and sets all
     * categories that belonged to the preset back to 'allow'.
     */
    public deactivatePreset(): void {
        const preset = this.activePreset;
        this.activePresetId = null;
        if (!preset) return;

        const presetCategoryNames = new Set(preset.categories.map(c => c.categoryName.toLowerCase()));
        this.bronzeCategories = this.bronzeCategories.map(c =>
            presetCategoryNames.has(c.name.toLowerCase()) ? {...c, action: 'allow'} : c
        );
        this.setDirty(true);
    }

    /**
     * Toggle a PowerDNS category between allow and block.
     * Auto-deactivates the active preset when:
     *   - a preset-owned category is set to 'allow', or
     *   - a non-preset category is set to 'block'.
     * @param categoryName  The category's technical name
     * @param isAllow       True = allow, false = block
     */
    public updateBronzeCategory(categoryName: string, isAllow: boolean) {
        this.bronzeCategories = this.bronzeCategories.map(c =>
            c.name === categoryName ? {...c, action: isAllow ? 'allow' : 'block'} : c
        );

        this.activePresetId = this.inferActivePresetId(this.bronzeCategories);
        this.setDirty(true);
        this.sendBronzeFwEvent(EventType.UpdateVersion, {
            updateType: BronzeTabName.CATEGORIES,
            categoryName,
            categoryAction: isAllow ? 'allow' : 'block',
        });
    }

    public blockAllCategories() {
        this.activePresetId = null;
        this.bronzeCategories = this.bronzeCategories.map(c => ({...c, action: 'block'}));
        this.setDirty(true);
    }

    public allowAllCategories() {
        this.activePresetId = null;
        this.bronzeCategories = this.bronzeCategories.map(c => ({...c, action: 'allow'}));
        this.setDirty(true);
    }

    public setDirty(isDirty: boolean) {
        runInAction(() => {
            this.isDirty = isDirty;
            if (isDirty) {
                this.setVersionState(BronzeVersionState.DRAFT);
            }
        });
    }

    public async fetchLatestVersion(versionNumber: string, versionState: VersionState) {
        const res = await bronzeService.getLatestVersion(this.rootStore.customerStore.customerId, this.rootStore.customerStore.serviceId);
        if (res.externalId !== versionNumber || res.versionState !== versionState) {
            this.updateSelectedVersion(res, true);
        }
        if (res.versionState === VersionState.ACTIVE) {
            if (res.externalId === versionNumber || res.baseVersionExternalId === versionNumber) {
                this.sendGeneralEvent(EventType.ActiveVersionChanged, {
                    versionId: versionNumber,
                    actionDuration: this.applyPolicyTime ? calcActionDuration(this.applyPolicyTime) : undefined
                });
                this.setVersionState(BronzeVersionState.APPLIED, { autoClear: true });
            }
            else {
                this.displayErrorAndClear();
                this.setVersionState(undefined); 
            }
        }
    }

    private sendBronzeFwEvent(eventType: EventType, extraInfo?: object): void {
        const {externalId, versionNumber, versionState, versionStatus} = this.selectedFwVersion || {};
        this.sendGeneralEvent(eventType, {externalId, versionNumber, versionState, versionStatus, ...extraInfo});
    }

    public sendBronzeUpdateEvent(updateType: BronzeTabName): void {
        this.sendBronzeFwEvent(EventType.UpdateVersion, {updateType});
    }


    public sendApplyPolicyEvent(versionId?: string,
                                extraInfo?: object): void {
        this.rootStore.fwVersionStore.sendFwEvent(EventType.ApplyPolicy, {
            versionId,
            isRecommended: false,
            ...extraInfo
        });
    }

    get getAddresses(): Address[] {
        return mapAddresses(this.selectedFwVersion?.content?.policy.addresses, this.selectedFwVersion?.content?.policy.address_groups) || [];
    }

    private deriveCategoryTitle(categoryName: string): string {
        return categoryName
            .replace(/_/g, ' ')
            .replace(/\b\w/g, ch => ch.toUpperCase());
    }

    private normalizePreset(preset: PresetDto): PresetDto {
        return {
            ...preset,
            categories: preset.categories
                .map(c => ({
                    ...c,
                    categoryTitle: this.deriveCategoryTitle(c.categoryName),
                }))
                .sort((a, b) => a.categoryTitle.localeCompare(b.categoryTitle)),
        };
    }

    /**
     * Scans the loaded presets and returns the groupId of the first preset whose
     * category set exactly matches the set of blocked categories in the supplied list.
     * "Exact match" means:
     *   - every preset category ID is blocked, AND
     *   - no category outside the preset is blocked.
     * Returns null when no preset matches (e.g. no presets loaded yet, or the
     * blocked set does not correspond to any preset).
     */
    private inferActivePresetId(categories: CategoryUrl[]): string | null {
        if (!this.presets.length || !categories.length) return null;
        const blockedNames = new Set(categories.filter(c => c.action === 'block').map(c => c.name.toLowerCase()));
        const match = this.presets.find(preset => {
            const presetNames = new Set(preset.categories.map(c => c.categoryName.toLowerCase()));
            if (presetNames.size !== blockedNames.size) return false;
            return Array.from(presetNames).every(name => blockedNames.has(name));
        });
        return match?.groupId ?? null;
    }

    private setLoading(isActive: boolean) {
        runInAction(() => {
            this.isLoading = isActive;
        });
    }

    public addAddress(address: Address) {
        if (!this.selectedFwVersion) return;
        if (!this.selectedFwVersion.content.policy.addresses) {
            this.selectedFwVersion.content.policy.addresses = [];
        }
        this.selectedFwVersion.content.policy.addresses.push(address);
        this.setDirty(true);
        this.sendBronzeFwEvent(EventType.UpdateVersion, {updateType: BronzeTabName.IP_EXCEPTIONS});
    }

    public updateAddress(updated: Address, original: Address) {
        if (!this.selectedFwVersion) return;
        const addresses = this.selectedFwVersion.content.policy.addresses;
        if (!addresses) return;
        const idx = addresses.findIndex(addr => addr.value === original.value);
        if (idx !== -1) {
            addresses[idx] = {...updated};
            this.setDirty(true);
            this.sendBronzeFwEvent(EventType.UpdateVersion, {updateType: BronzeTabName.IP_EXCEPTIONS});
        }
    }

    public removeAddress(address: Address) {
        if (!this.selectedFwVersion) return;
        const addresses = this.selectedFwVersion.content.policy.addresses;
        if (!addresses) return;
        this.selectedFwVersion.content.policy.addresses = addresses.filter(addr => addr.value !== address.value);
        this.setDirty(true);
        this.sendBronzeFwEvent(EventType.UpdateVersion, {updateType: BronzeTabName.IP_EXCEPTIONS});
    }

    public hasReachedDomainLimitFor(actionType: string) {
        const domainsCount = this.selectedVersionDomains.filter(domain => domain.domainAction === actionType).length;
        return domainsCount === MAX_BRONZE_DOMAINS / 2;
    }

    public addDomain(domain: Domain) {
        this.selectedVersionDomains = [...this.selectedVersionDomains, domain];
        this.setDirty(true);
        this.sendBronzeFwEvent(EventType.UpdateVersion, {updateType: BronzeTabName.DOMAINS});
    }

    public updateDomain(updated: Domain, original: Domain) {
        const domains = this.selectedVersionDomains;
        const idx = domains.findIndex(d => d.domainUrl === original.domainUrl);
        if (idx !== -1) {
            domains[idx] = {...updated};
            this.selectedVersionDomains = [...domains];
            this.setDirty(true);
            this.sendBronzeFwEvent(EventType.UpdateVersion, {updateType: BronzeTabName.DOMAINS});
        }
    }

    public removeDomain(domain: Domain) {
        const fullDomains = this.selectedVersionDomains;
        this.selectedVersionDomains = fullDomains.filter(d => d.domainUrl !== domain.domainUrl);
        this.setDirty(true);
        this.sendBronzeFwEvent(EventType.UpdateVersion, {updateType: BronzeTabName.DOMAINS});
    }

    public async deactivateReactivateDnsProtection(isDeactivate: boolean) {
        try {
            this.setLoading(true);
            const updatedVersion = await bronzeService.deactivateDnsProtection(this.rootStore.customerStore.customerId, this.rootStore.customerStore.serviceId,
                this.selectedFwVersion?.externalId ?? '', isDeactivate);
            this.updateSelectedVersion(updatedVersion, true);
            this.setLoading(false);
        } catch (e) {
            this.setLoading(false);
            throw e;
        }
    }

}